import {
  KdsModifierStyle,
  ModifierKeywordMatchMode,
} from '@oolio-group/domain';

export interface MatchedModifierStyle {
  color?: string;
  italic?: boolean;
}

/**
 * Returns the active modifier styles in the precedence order matching uses:
 * ascending `priority` (the same order the settings list shows them). Inactive
 * styles (`isActive === false`) are dropped. Callers on a hot render path can
 * memoize this once per `styles` reference and pass the result to
 * {@link matchInSortedModifierStyles} instead of re-sorting per modifier.
 */
export function sortActiveModifierStyles(
  styles: KdsModifierStyle[] | undefined | null,
): KdsModifierStyle[] {
  return (styles ?? [])
    .filter(style => style.isActive !== false)
    .slice()
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Finds the modifier style that should apply to a modifier line, given the
 * already-sorted active styles. The first style whose keyword matches wins; a
 * modifier that matches none returns `undefined`.
 */
export function matchInSortedModifierStyles(
  modifierName: string | undefined,
  sortedActiveStyles: KdsModifierStyle[],
): MatchedModifierStyle | undefined {
  const normalized = (modifierName || '').trim().toLowerCase();
  if (!normalized || !sortedActiveStyles.length) return undefined;

  for (const style of sortedActiveStyles) {
    const matches = style.conditions?.some(condition => {
      const keyword = (condition?.keyword || '').trim().toLowerCase();
      if (!keyword) return false;
      return condition.matchType === ModifierKeywordMatchMode.EXACT
        ? normalized === keyword
        : normalized.includes(keyword);
    });

    if (matches) {
      return { color: style.styles?.color, italic: style.styles?.italic };
    }
  }

  return undefined;
}

/**
 * Finds the modifier style that should apply to a modifier line on a docket.
 *
 * Styles are considered in ascending `priority` order (the same order the
 * settings list shows them), and the first active style whose keyword matches
 * the modifier text wins. A modifier that matches no style returns `undefined`
 * and keeps its default appearance.
 */
export function matchModifierStyle(
  modifierName: string | undefined,
  styles: KdsModifierStyle[] | undefined | null,
): MatchedModifierStyle | undefined {
  return matchInSortedModifierStyles(
    modifierName,
    sortActiveModifierStyles(styles),
  );
}
